import { useState, useCallback, memo, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthActions } from "../contexts/AuthContext";
import { Eye, EyeOff } from "lucide-react";
import { FormErrorSlot } from "../components/FormErrorSlot";
import { AuthLayout } from "../components/AuthLayout";
import { AuthFormShell } from "../components/AuthFormShell";
import { AuthFormInput } from "../components/AuthFormInput";
import { StableSubmitButton } from "../components/StableSubmitButton";
import { AuthFormFooter } from "../components/AuthFormFooter";
import { TermsAcceptanceCheckbox } from "../components/TermsAcceptanceCheckbox";
import { APP_LOGIN, LOGIN_REALM } from "../lib/authRouting";

function PasswordField({
    value,
    onChange,
    showPwd,
    setShowPwd,
    testId,
    autoComplete = "current-password",
    placeholder = "Your password",
}) {
    return (
        <div className="relative">
            <AuthFormInput
                type={showPwd ? "text" : "password"}
                value={value}
                onChange={onChange}
                required
                autoComplete={autoComplete}
                placeholder={placeholder}
                className="pr-12"
                data-testid={testId}
            />
            <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute inset-y-0 right-3 my-auto text-white/40 hover:text-white transition-colors"
                data-testid={`${testId}-toggle`}
                tabIndex={-1}
                aria-label={showPwd ? "Hide password" : "Show password"}
            >
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
        </div>
    );
}

function LoginFormFields({
    email,
    setEmail,
    password,
    setPassword,
    showPwd,
    setShowPwd,
    err,
    busy,
    onSubmit,
    testIdPrefix,
    showRegisterLink,
}) {
    return (
        <AuthFormShell
            formTestId={testIdPrefix === "admin-login" ? "admin-login-form" : "login-form"}
            onSubmit={onSubmit}
            error={err}
            errorTestId="login-error"
            busy={busy}
            idleLabel={testIdPrefix === "admin-login" ? "Access console" : "Sign in"}
            busyLabel="Signing in…"
            submitTestId={`${testIdPrefix}-submit`}
            footerTestId="login-footer"
            footer={
                showRegisterLink ? (
                    <>
                        <span>No account yet?</span>
                        <Link to="/register" className="auth-form__footer-link" data-testid="link-register">
                            Create account
                        </Link>
                    </>
                ) : null
            }
            fields={
                <>
                    <label className="auth-field">
                        <span className="label-eyebrow">Email</span>
                        <AuthFormInput
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                            placeholder="you@company.com"
                            data-testid="login-email-input"
                        />
                    </label>
                    <label className="auth-field">
                        <span className="label-eyebrow">Password</span>
                        <PasswordField
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            showPwd={showPwd}
                            setShowPwd={setShowPwd}
                            testId="login-password-input"
                        />
                    </label>
                </>
            }
        />
    );
}

function TwoFactorForm({ code, setCode, err, busy, onSubmit, onBack }) {
    return (
        <form onSubmit={onSubmit} className="auth-form" data-testid="login-2fa-form">
            <div className="auth-form__fields">
                <AuthFormInput
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                    maxLength={6}
                    placeholder="• • • • • •"
                    className="py-3 text-center text-xl font-mono tracking-[0.4em]"
                    data-testid="login-2fa-input"
                />
            </div>
            <FormErrorSlot message={err} testId="login-2fa-error" />
            <StableSubmitButton
                busy={busy}
                idleLabel="Verify & continue"
                busyLabel="Verifying…"
                disabled={code.length < 6}
                testId="login-2fa-submit"
            />
            <AuthFormFooter testId="login-2fa-footer">
                <button
                    type="button"
                    onClick={onBack}
                    className="border-0 bg-transparent p-0 text-sm text-white/45 cursor-pointer transition-colors hover:text-white"
                >
                    Back to sign in
                </button>
            </AuthFormFooter>
        </form>
    );
}

function LoginPageInner({ adminMode = false }) {
    const navigate = useNavigate();
    const { login, verify2fa } = useAuthActions();
    const loginRealm = adminMode ? LOGIN_REALM.ADMIN : LOGIN_REALM.APP;
    const testIdPrefix = adminMode ? "admin-login" : "login";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [twoFa, setTwoFa] = useState(null);
    const [code, setCode] = useState("");

    const handleLoginFailure = useCallback((res) => {
        if (res.redirectTo) {
            navigate(res.redirectTo, { replace: true });
            return;
        }
        setErr(res.error || "Login failed");
        setBusy(false);
    }, [navigate]);

    const onSubmit = useCallback(async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        const res = await login(email, password, loginRealm);
        if (res.two_factor_required) {
            setTwoFa({ challenge_token: res.challenge_token, loginRealm: res.loginRealm || loginRealm });
            setBusy(false);
        } else if (res.rejected || !res.ok) {
            handleLoginFailure(res);
        } else if (res.redirectTo) {
            navigate(res.redirectTo, { replace: true });
        } else {
            setBusy(false);
        }
    }, [email, password, login, loginRealm, handleLoginFailure, navigate]);

    const onSubmit2fa = useCallback(async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        const realm = twoFa?.loginRealm || loginRealm;
        const res = await verify2fa(twoFa.challenge_token, code, realm);
        if (res.rejected || !res.ok) {
            handleLoginFailure(res);
        } else if (res.redirectTo) {
            navigate(res.redirectTo, { replace: true });
        } else {
            setBusy(false);
        }
    }, [twoFa, code, verify2fa, loginRealm, handleLoginFailure, navigate]);

    const title = twoFa ? "Two-factor required" : adminMode ? "System access" : "Welcome back";
    const subtitle = twoFa
        ? "Enter the 6-digit code from your authenticator app."
        : adminMode
            ? "Enter superadmin credentials to access the platform console."
            : "Sign in to your FairShare workspace.";

    return (
        <AuthLayout title={title} subtitle={subtitle} variant={adminMode ? "admin" : "app"} loginPage>
            {twoFa ? (
                <TwoFactorForm
                    code={code}
                    setCode={setCode}
                    err={err}
                    busy={busy}
                    onSubmit={onSubmit2fa}
                    onBack={() => { setTwoFa(null); setCode(""); setErr(""); }}
                />
            ) : (
                <LoginFormFields
                    email={email}
                    setEmail={setEmail}
                    password={password}
                    setPassword={setPassword}
                    showPwd={showPwd}
                    setShowPwd={setShowPwd}
                    err={err}
                    busy={busy}
                    onSubmit={onSubmit}
                    testIdPrefix={testIdPrefix}
                    showRegisterLink={!adminMode}
                />
            )}
        </AuthLayout>
    );
}

export const LoginPage = memo(LoginPageInner);

export function RegisterPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { register } = useAuthActions();
    const [name, setName] = useState("");
    const [orgName, setOrgName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showPwd, setShowPwd] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        const prefillEmail = searchParams.get("email");
        if (prefillEmail) setEmail(prefillEmail);
    }, [searchParams]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!acceptedTerms) {
            setErr("You must accept the Terms of Service to create an account.");
            return;
        }
        setBusy(true);
        setErr("");
        const res = await register(name, email, password, orgName, acceptedTerms);
        if (res.rejected || !res.ok) {
            if (res.redirectTo) {
                navigate(res.redirectTo, { replace: true });
                return;
            }
            setErr(res.error || "Registration failed");
            setBusy(false);
        } else if (res.redirectTo) {
            navigate(res.redirectTo, { replace: true });
        } else {
            setBusy(false);
        }
    };

    return (
        <AuthLayout title="Create your workspace" subtitle="Start splitting profit in under 2 minutes." wide>
            <AuthFormShell
                formTestId="register-form"
                onSubmit={onSubmit}
                error={err}
                errorTestId="register-error"
                busy={busy}
                idleLabel="Create workspace"
                busyLabel="Creating…"
                submitTestId="register-submit"
                submitDisabled={!acceptedTerms}
                footerTestId="register-footer"
                footer={
                    <>
                        <span>Already have an account?</span>
                        <Link to={APP_LOGIN} className="auth-form__footer-link" data-testid="link-login">
                            Back to login
                        </Link>
                    </>
                }
                fields={
                    <>
                        <div className="auth-form__row-2">
                            <label className="auth-field">
                                <span className="label-eyebrow">Your name</span>
                                <AuthFormInput
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    autoComplete="name"
                                    placeholder="Your name"
                                    data-testid="register-name-input"
                                />
                            </label>
                            <label className="auth-field">
                                <span className="label-eyebrow">Workspace</span>
                                <AuthFormInput
                                    value={orgName}
                                    onChange={(e) => setOrgName(e.target.value)}
                                    placeholder="My Company"
                                    autoComplete="organization"
                                    data-testid="register-org-input"
                                />
                            </label>
                        </div>
                        <label className="auth-field">
                            <span className="label-eyebrow">Work email</span>
                            <AuthFormInput
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                placeholder="you@company.com"
                                data-testid="register-email-input"
                            />
                        </label>
                        <label className="auth-field">
                            <span className="label-eyebrow">Password</span>
                            <PasswordField
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                showPwd={showPwd}
                                setShowPwd={setShowPwd}
                                testId="register-password-input"
                                autoComplete="new-password"
                                placeholder="Create a password"
                            />
                            <p className="mt-1 text-[11px] leading-relaxed text-white/30">6+ characters</p>
                        </label>
                        <TermsAcceptanceCheckbox
                            checked={acceptedTerms}
                            onChange={setAcceptedTerms}
                            id="register-accept-terms"
                            testId="register-terms-checkbox"
                        />
                    </>
                }
            />
        </AuthLayout>
    );
}
